import { millify } from '@velocity-exchange/common';

const VERY_SMALL_NUMBER_LEADING_ZEROES = 3;
const VERY_LARGE_NUMBER_LEADING_DIGITS = 6;

interface HandleSpecialRenderingOptions {
	/**
	 * Defaults to 'auto'.
	 */
	mode?: 'off' | 'auto' | 'small' | 'large';
	/**
	 * Number of decimals to use for the large value.
	 */
	decimals?: number;
	/**
	 * Returned when the value is not special rendered.
	 */
	fallback?: string;
}

export default function handleSpecialRendering(
	value: string,
	opts?: HandleSpecialRenderingOptions
) {
	const { mode = 'auto', decimals, fallback } = opts ?? {};

	if (!value || mode === 'off') {
		return fallback ?? value;
	}

	const shouldRenderSmall = mode === 'auto' || mode === 'small';

	if (shouldRenderSmall && isVerySmallValue(value)) {
		return renderVerySmallValue(value);
	}

	const shouldRenderLarge = mode === 'auto' || mode === 'large';

	if (shouldRenderLarge && isVeryLargeValue(value)) {
		return renderVeryLargeValue(value, undefined, decimals);
	}

	return fallback ?? value;
}

function numberToUnicodeSubscript(value: number) {
	const subscriptMap: Record<string, string> = {
		'0': '₀',
		'1': '₁',
		'2': '₂',
		'3': '₃',
		'4': '₄',
		'5': '₅',
		'6': '₆',
		'7': '₇',
		'8': '₈',
		'9': '₉',
	};

	return value
		.toString()
		.split('')
		.map((character) => subscriptMap[character] || character)
		.join('');
}

function isVerySmallValue(value: string): boolean {
	if (!value.includes('.')) {
		return false;
	}

	const splitValue = value.split('.');
	if (splitValue[1].match(/^0+$/)) {
		return false;
	}

	const leadingZeroes = splitValue[1].match(/^0+/)?.[0].length ?? 0;
	return leadingZeroes > VERY_SMALL_NUMBER_LEADING_ZEROES;
}

function isVeryLargeValue(value: string): boolean {
	return value.split('.')[0].length > VERY_LARGE_NUMBER_LEADING_DIGITS;
}

function renderVerySmallValue(value: string) {
	const splitValue = value.split('.');
	if (!splitValue[1] || !isVerySmallValue(value)) {
		return value;
	}

	const leadingZeroes = splitValue[1].match(/^0+/)?.[0].length ?? 0;
	const valueAfterLeadingZeroes = splitValue[1].slice(leadingZeroes);
	if (splitValue[1].match(/^0+$/)) {
		return value;
	}

	return `${splitValue[0]}.0${numberToUnicodeSubscript(leadingZeroes)}${valueAfterLeadingZeroes}`;
}

function renderVeryLargeValue(value: string, precision = 6, decimals?: number) {
	if (!isVeryLargeValue(value)) {
		return value;
	}

	return millify(Number(value), {
		precision,
		decimals,
		trimEndingZeroes: true,
	});
}
